import { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { imageSrc } from "@/lib/api";
import { initTheme } from "@/lib/theme";
import { Eyebrow } from "@/components/Eyebrow";
import { Link as LinkIcon, Loader2, FileWarning, Eye, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Section({ title, testId, children }) {
  return (
    <div style={{ marginTop: 40 }} data-testid={testId}>
      <Eyebrow>{title}</Eyebrow>
      <div style={{ marginTop: 16 }}>{children}</div>
    </div>
  );
}

function Chips({ items, testId }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid={testId}>
      {items.map((m, i) => (
        <span key={`${m}-${i}`} className="font-mono" style={{ fontSize: 12, padding: "5px 11px", borderRadius: 999, border: "1px solid var(--cardhover)", color: "var(--tsecondary)" }}>{m}</span>
      ))}
    </div>
  );
}

export default function BrandKitPublic() {
  const { shareId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    initTheme();
    axios
      .get(`${BACKEND_URL}/api/public/brandkit/${shareId}`)
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch(() => setState({ loading: false, error: "not_found", data: null }));
  }, [shareId]);

  if (state.loading) {
    return (
      <div className="lightboard" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="brandkit-loading">
        <Loader2 size={26} className="spin" style={{ color: "var(--orange)" }} />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="lightboard" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 24, textAlign: "center" }} data-testid="brandkit-notfound">
        <FileWarning size={34} style={{ color: "var(--orange)" }} />
        <h1 className="font-display" style={{ fontSize: 22, fontWeight: 700, color: "var(--tprimary)", margin: 0 }}>Brand kit unavailable</h1>
        <p className="font-body" style={{ fontSize: 14, color: "var(--tsecondary)", maxWidth: 360 }}>This link is invalid or sharing has been turned off. Please ask for an updated link.</p>
      </div>
    );
  }

  const b = state.data.brand;
  const assets = state.data.assets || [];
  const views = state.data.views || 0;

  return (
    <div className="lightboard" style={{ minHeight: "100vh" }} data-testid="brandkit-public">
      <header className="cline-b" style={{ position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 24px", background: "var(--surface)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/hdg-logo.jpg" alt="HeyDesignGuy" style={{ height: 30, width: 30, objectFit: "contain", borderRadius: 6, background: "#fff", padding: 3 }} />
          <span className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)", letterSpacing: "0.08em" }}>BRAND KIT</span>
        </div>
        <span className="font-mono" data-testid="brandkit-views" title="Total views" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--tmuted)" }}>
          <Eye size={13} /> {views.toLocaleString()}
        </span>
      </header>

      <main style={{ maxWidth: 920, margin: "0 auto", padding: "40px 24px 80px" }}>
        <h1 className="font-display" data-testid="brandkit-brand-name" style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", color: "var(--tprimary)", margin: 0, lineHeight: 1.05 }}>
          {b.brandName || b.siteName}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
          {b.industry && <span className="font-mono" style={{ fontSize: 12, color: "var(--orange)" }}>{b.industry}</span>}
          {b.sourceUrl && (
            <a href={b.sourceUrl} target="_blank" rel="noreferrer" className="font-mono" style={{ fontSize: 12, color: "var(--tsecondary)", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
              <LinkIcon size={12} /> {b.sourceUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </a>
          )}
        </div>

        {(b.colors || []).length > 0 && (
          <Section title="Palette" testId="brandkit-palette">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {b.colors.map((c, i) => (
                <div key={`${c}-${i}`} style={{ textAlign: "center" }}>
                  <div style={{ width: 64, height: 64, borderRadius: 10, background: c, border: "1px solid rgba(255,255,255,0.08)" }} />
                  <div className="font-mono" style={{ fontSize: 11, color: "var(--tsecondary)", marginTop: 6 }}>{c}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {(b.fonts || []).length > 0 && (
          <Section title="Typefaces" testId="brandkit-typefaces">
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {b.fonts.map((f, i) => (
                <div key={`${f}-${i}`}>
                  <div style={{ fontFamily: `'${f}', Inter, sans-serif`, fontSize: 28, color: "var(--tprimary)", lineHeight: 1.1 }}>{f}</div>
                  <div className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)" }}>Aa Bb Cc 0123</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {b.voiceSummary && (
          <Section title="Voice" testId="brandkit-voice">
            <p className="font-body" style={{ fontSize: 16, color: "var(--tsecondary)", lineHeight: 1.65, margin: 0, maxWidth: 680 }}>{b.voiceSummary}</p>
          </Section>
        )}

        {(b.keyMessages || []).length > 0 && (
          <Section title="Key Messages" testId="brandkit-key-messages">
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {b.keyMessages.map((m, i) => (
                <li key={i} className="font-body" style={{ fontSize: 15, color: "var(--tprimary)", display: "flex", gap: 10 }}>
                  <span className="font-mono" style={{ color: "var(--orange)" }}>0{i + 1}</span> {m}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {((b.visualMood || []).length > 0 || b.targetAudience) && (
          <Section title="Positioning" testId="brandkit-positioning">
            {b.targetAudience && (
              <div style={{ marginBottom: 16 }}>
                <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Target Audience</div>
                <p className="font-body" style={{ fontSize: 15, color: "var(--tsecondary)", margin: 0, lineHeight: 1.6, maxWidth: 680 }}>{b.targetAudience}</p>
              </div>
            )}
            {(b.visualMood || []).length > 0 && <Chips items={b.visualMood} testId="brandkit-mood" />}
          </Section>
        )}

        {assets.length > 0 && (
          <Section title="Creative Assets" testId="brandkit-assets">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
              {assets.map((g) => {
                const cover = (g.items || [])[0];
                const multi = (g.items || []).length > 1;
                return (
                  <div key={g.groupId} className="panel-card" style={{ padding: 8, overflow: "hidden" }} data-testid={`brandkit-asset-${g.groupId}`}>
                    <div style={{ borderRadius: 8, overflow: "hidden", aspectRatio: "1/1", background: "var(--input)" }}>
                      {cover && <img src={imageSrc(cover.url)} alt={g.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                    </div>
                    <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginTop: 8, display: "flex", justifyContent: "space-between" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title || g.assetType}</span>
                      <span style={{ flexShrink: 0 }}>{multi ? `${g.items.length} slides` : g.aspect}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        <div
          data-testid="brandkit-cta"
          style={{ marginTop: 56, padding: "32px 28px", borderRadius: 16, border: "1px solid var(--cardhover)", background: "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(249,115,22,0.02))", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 14 }}
        >
          <h2 className="font-display" style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--tprimary)", margin: 0 }}>
            Want a brand kit like this?
          </h2>
          <p className="font-body" style={{ fontSize: 14.5, color: "var(--tsecondary)", lineHeight: 1.6, margin: 0, maxWidth: 460 }}>
            HDG Creative Studio turns any website into on-brand social content in minutes — analysis, ideas, visuals and scheduling.
          </p>
          <a
            data-testid="brandkit-cta-button"
            className="btn-primary"
            href="https://heydesignguy.com"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", padding: "11px 22px", marginTop: 4 }}
          >
            Build your own with HeyDesignGuy <ArrowRight size={15} />
          </a>
        </div>
      </main>

      <footer className="cline-t" style={{ padding: "16px 24px", textAlign: "center" }}>
        <a className="app-link font-body" style={{ fontSize: 12 }} href="https://heydesignguy.com" target="_blank" rel="noreferrer">
          Made with HDG Creative Studio — heydesignguy.com
        </a>
      </footer>
    </div>
  );
}
